/**
 * SettingsSaveBar — the Console's single, sticky "unsaved changes" bar.
 *
 * Bottom-pinned inside the panel column (the approved Concept-A mockup), a
 * sibling of the house PageActionBar: same translucent backdrop-blur chrome,
 * but anchored to the bottom of the SidebarInset scroll surface and only
 * visible while at least one section is dirty.
 */
import { Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useSettingsDirty } from './SettingsDirtyContext';

export function SettingsSaveBar() {
  const { count, labels, isSaving, saveAll, discardAll } = useSettingsDirty();

  if (count === 0) return null;

  const summary =
    labels.length <= 2 ? labels.join(', ') : `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;

  const handleSave = async () => {
    const { saved, failed } = await saveAll();
    if (failed.length === 0) {
      toast.success(saved === 1 ? 'Change saved' : `${saved} changes saved`);
    } else {
      toast.error(`Could not save: ${failed.join(', ')}`);
    }
  };

  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      className="sticky bottom-4 z-40 mt-4 rounded-lg border border-border/70 bg-background/90 supports-[backdrop-filter]:bg-background/75 backdrop-blur shadow-lg"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5">
        <span className="flex items-center gap-2 text-sm font-medium">
          <span
            aria-hidden
            className="h-2 w-2 rounded-full bg-amber-500 shadow-[0_0_0_3px_rgb(245_158_11_/_0.2)]"
          />
          {count === 1 ? '1 unsaved change' : `${count} unsaved changes`}
        </span>
        <span className="hidden sm:inline text-xs text-muted-foreground truncate max-w-[40ch]">
          {summary}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={discardAll} disabled={isSaving}>
            Discard
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
            className="gap-2 bg-threadheads-purple hover:bg-threadheads-purple/90 text-white"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
